/*
 * rd-product.js
 *
 * Piezas de la página de producto que el JS de Dawn NO puede cubrir, porque
 * Shrine usa un marcado distinto (el Dawn moderno usa data-option-value-id y
 * un elemento <product-info> que Shrine no tiene):
 *
 *   <variant-selects>  selector de variantes (pills, dropdowns, swatches)
 *   <sticky-atc>       botón "Agregar al carrito" fijo
 *
 * El carrito, el drawer, <quantity-input> y <product-form> los maneja el JS de
 * Dawn (cart.js, cart-drawer.js, product-form.js, rd-dawn-core.js).
 */
(function () {
  'use strict';

  // El tema quita la clase "no-js" desde el main.js externo. Si ese no corre,
  // el CSS deja escondidos los botones + / -. Lo forzamos aquí.
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  /* ================================================================== */
  /* <variant-selects>                                                   */
  /* ================================================================== */

  function defineVariantSelects() {
    if (customElements.get('variant-selects')) return;

    customElements.define(
      'variant-selects',
      class extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;
          this.variants = this.readVariants();
          if (!this.variants.length) return;

          this.addEventListener('change', this.onChange.bind(this));
          this.apply(false);
        }

        readVariants() {
          var tag = this.querySelector('script[type="application/json"]');
          if (!tag) return [];
          try {
            return JSON.parse(tag.textContent);
          } catch (e) {
            console.error('[rd-product] variantes ilegibles:', e);
            return [];
          }
        }

        /*
         * Solo los grupos que son de verdad una opción de variante.
         * Antes contaba grupos de más, la variante nunca coincidía, y por eso
         * el botón de compra terminaba desactivado. Ese era el bug.
         */
        optionGroups() {
          return Array.prototype.filter.call(
            this.querySelectorAll('.product-form__input'),
            function (group) {
              if (group.classList.contains('product-form__input--quantity-breaks')) { var vsEl = group.closest('variant-selects'); var r0 = group.querySelector('input[type="radio"]'); return !!(vsEl && r0 && (vsEl.variants || []).some(function (v) { return v.options.indexOf(r0.value) !== -1; })); }
              return !!(
                group.querySelector('select[name^="options"]') ||
                group.querySelector('input[type="radio"]')
              );
            }
          );
        }

        selectedOptions() {
          return this.optionGroups()
            .map(function (group) {
              var select = group.querySelector('select[name^="options"]');
              if (select) return select.value;
              var radio = group.querySelector('input[type="radio"]:checked');
              return radio ? radio.value : null;
            })
            .filter(function (value) {
              return value !== null;
            });
        }

        matchVariant() {
          var chosen = this.selectedOptions();
          if (!chosen.length) return null;

          return (
            this.variants.find(function (variant) {
              return chosen.every(function (value, i) {
                return variant.options[i] === value;
              });
            }) || null
          );
        }

        onChange() {
          this.apply(true);
        }

        apply(userDriven) {
          var variant = this.matchVariant();

          // Si no logramos emparejar, NO tocamos nada. Preferimos que se pueda
          // seguir comprando antes que bloquear la tienda por un fallo nuestro.
          if (!variant) return;

          this.writeVariantId(variant);
          this.setAvailability(variant);

          if (!userDriven) return;

          if (this.dataset.updateUrl !== 'false') {
            window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
          }
          this.refreshPrice(variant);
        }

        /*
         * EL ARREGLO CENTRAL: escribir la variante elegida en el campo oculto
         * que se envía al carrito. Sin esto siempre se agregaba la primera.
         */
        writeVariantId(variant) {
          document.querySelectorAll('.product-variant-id').forEach(function (input) {
            input.value = variant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }

        /*
         * Solo HABILITAMOS. Nunca desactivamos el botón: si nuestra lógica se
         * equivoca al emparejar la variante, el peor caso debe ser un mensaje
         * de error de Shopify, jamás una tienda donde no se puede comprar.
         * (Desactivarlo aquí fue justo el bug que mató el botón de compra.)
         */
        setAvailability(variant) {
          if (!variant.available) return;

          var buttons = document.querySelectorAll(
            '#ProductForm-' + this.sectionId + ' [type="submit"], ' +
              '#sticky-atc-' + this.sectionId + ' button[type="submit"]'
          );

          buttons.forEach(function (button) {
            button.disabled = false;
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
          });
        }

        refreshPrice(variant) {
          var url = this.dataset.url + '?variant=' + variant.id + '&section_id=' + this.sectionId;

          fetch(url)
            .then(function (res) { return res.text(); })
            .then(
              function (html) {
                var doc = new DOMParser().parseFromString(html, 'text/html');
                ['price-', 'sticky-atc-price-', 'sticky-atc-separate-price-'].forEach(
                  function (prefix) {
                    var id = prefix + this.sectionId;
                    var fresh = doc.getElementById(id);
                    var current = document.getElementById(id);
                    if (fresh && current) current.innerHTML = fresh.innerHTML;
                  }.bind(this)
                );
              }.bind(this)
            )
            .catch(function (err) {
              console.error('[rd-product] precio:', err);
            });
        }
      }
    );
  }

  /* ================================================================== */
  /* <sticky-atc>                                                        */
  /* ================================================================== */

  function defineStickyAtc() {
    if (customElements.get('sticky-atc')) return;

    customElements.define(
      'sticky-atc',
      class extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;

          this.addEventListener('change', this.mirrorToMainPicker.bind(this));

          var button = this.querySelector('button');
          if (button) button.addEventListener('click', this.onClick.bind(this));

          if (this.dataset.afterScroll === 'true') {
            this.mainButton = document.querySelector(
              '#ProductForm-' + this.sectionId + ' [type="submit"]'
            );
            this.handleScroll = this.handleScroll.bind(this);
            window.addEventListener('scroll', this.handleScroll, { passive: true });
            this.handleScroll();
          }
        }

        disconnectedCallback() {
          if (this.handleScroll) window.removeEventListener('scroll', this.handleScroll);
        }

        handleScroll() {
          if (!this.mainButton) return;
          var passed = this.mainButton.getBoundingClientRect().bottom < 0;
          this.dataset.afterScroll = passed ? 'false' : 'true';
        }

        mirrorToMainPicker(event) {
          var picker = document.getElementById('variant-selects-' + this.sectionId);
          if (!picker) return;

          var value = event.target.value;
          var changed = false;

          picker.querySelectorAll('.product-form__input').forEach(function (group) {
            var select = group.querySelector('select[name^="options"]');
            if (select) {
              var exists = Array.prototype.some.call(select.options, function (option) {
                return option.value === value;
              });
              if (exists && select.value !== value) {
                select.value = value;
                changed = true;
              }
              return;
            }
            group.querySelectorAll('input[type="radio"]').forEach(function (radio) {
              if (radio.value === value && !radio.checked) {
                radio.checked = true;
                changed = true;
              }
            });
          });

          if (changed) picker.dispatchEvent(new Event('change', { bubbles: true }));
        }

        onClick(event) {
          if (this.dataset.scrollBtn === 'true') {
            event.preventDefault();
            this.scrollToDestination();
            return;
          }

          // Delegamos en el botón principal: product-form.js (Dawn) ya sabe
          // agregar al carrito por AJAX y abrir el drawer.
          event.preventDefault();
          var main = document.querySelector('#ProductForm-' + this.sectionId + ' [type="submit"]');
          if (main) main.click();
        }

        scrollToDestination() {
          var map = {
            product_information: '#ProductInfo-' + this.sectionId,
            variant_picker: '#variant-selects-' + this.sectionId,
            quantity_breaks: '.product-form__input--quantity-breaks',
            pricing_table: '.quantity-breaks'
          };
          var target = document.querySelector(map[this.dataset.scrollDestination] || '');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    );
  }

  /* ================================================================== */
  /* "Skip cart" — ir directo al checkout                                */
  /* ================================================================== */

  /*
   * Dos botones dependían de un manejador que vivía en el main.js externo:
   *
   *   1. El botón secundario "COMPRAR AHORA":
   *        <button type="button" data-skip-cart-button="true">
   *      Es type="button", así que NO envía el formulario. Sin JS no hacía nada.
   *
   *   2. El toggle "Skip cart" del botón principal, que marca el campo oculto
   *      con data-skip-cart="true".
   *
   * En ambos casos: agregar al carrito y mandar al checkout.
   */

  var CHECKOUT_URL = '/checkout';

  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  function showFormError(productForm, message) {
    var wrapper = productForm.querySelector('.product-form__error-message-wrapper');
    var text = productForm.querySelector('.product-form__error-message');
    if (wrapper && text) {
      text.textContent = message;
      wrapper.removeAttribute('hidden');
    } else {
      console.error('[rd-product]', message);
    }
  }

  function addThenCheckout(productForm, form, button) {
    if (button) button.classList.add('loading');

    return fetch(cartAddUrl() + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          return { ok: res.ok, json: json };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          showFormError(
            productForm,
            result.json.description || result.json.message || 'No se pudo agregar al carrito.'
          );
          if (button) button.classList.remove('loading');
          return;
        }
        window.location.href = CHECKOUT_URL;
      })
      .catch(function (err) {
        console.error('[rd-product] skip cart:', err);
        showFormError(productForm, 'No se pudo procesar la compra. Inténtalo de nuevo.');
        if (button) button.classList.remove('loading');
      });
  }

  function initSkipCart() {
    // 1) Botón secundario "COMPRAR AHORA".
    document.addEventListener('click', function (event) {
      var button =
        event.target.closest && event.target.closest('[data-skip-cart-button="true"]');
      if (!button || button.disabled) return;

      var productForm = button.closest('product-form');
      var form = productForm && productForm.querySelector('form');
      if (!form) return;

      event.preventDefault();
      addThenCheckout(productForm, form, button);
    });

    // 2) Botón principal cuando el ajuste "Skip cart" está activado.
    //    Capturamos ANTES que product-form.js (Dawn) y lo cortamos, para que
    //    no abra el drawer en vez de ir al checkout.
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        var idInput = form.querySelector('.product-variant-id[data-skip-cart="true"]');
        if (!idInput) return;

        var productForm = form.closest('product-form');
        if (!productForm) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        addThenCheckout(productForm, form, form.querySelector('[type="submit"]'));
      },
      true // fase de captura: corremos primero
    );
  }

  defineVariantSelects();
  defineStickyAtc();
  initSkipCart();
})();
